import { DefaultApi } from '../autogenerated/spdApiClient/apis/DefaultApi';
import {
  GetSynevoDto,
  GetSynevoExamCostSummaryDto,
} from '../autogenerated/spdApiClient';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { synevoForGrid } from '../../../spd/components/Synevo/SynevoSearch';

export interface SynevoContextInterface {
  get: (id: string) => Promise<GetSynevoDto>;
  getAll: () => Promise<GetSynevoDto[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    setRefresh?: () => void,
  ) => Promise<GridRecord[]>;
  getWaitingCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getWaitingByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    setRefresh?: () => void,
  ) => Promise<GridRecord[]>;
  getReadyCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getReadyByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    setRefresh?: () => void,
  ) => Promise<GridRecord[]>;
  getForExamCount: (
    examId: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getForExamByPortion: (
    examId: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    setRefresh?: () => void,
  ) => Promise<GridRecord[]>;
  cancelOrder: (synevoId: string, sentOrderId: string) => Promise<void>;
  createForExam: (
    examId: string,
    supportingExaminationDictIds?: string[],
  ) => Promise<void>;
  getForExamSummary: (examId: string) => Promise<GetSynevoExamCostSummaryDto>;
  getDictCost: (
    dictIds: string[],
  ) => Promise<{ count: number; amount: string }>;
}

export const SynevoContext = (api: DefaultApi) => {
  return {
    get: async (id: string) => {
      return await api.synevoControllerGet(id);
    },

    getAll: () => api.synevoControllerGetAll(),

    getAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      setRefresh?: () => void,
    ) => {
      const data = await api.synevoControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(synevo => synevoForGrid(synevo, setRefresh));
    },

    getAllCount: (filterText?: string, filterColumns?: string) =>
      api.synevoControllerGetAllCount(filterText || '', filterColumns || ''),

    getWaitingByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      setRefresh?: () => void,
    ) => {
      const data = await api.synevoControllerGetWaitingByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(synevo => synevoForGrid(synevo, setRefresh));
    },

    getWaitingCount: (filterText?: string, filterColumns?: string) =>
      api.synevoControllerGetWaitingCount(
        filterText || '',
        filterColumns || '',
      ),

    getReadyByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      setRefresh?: () => void,
    ) => {
      const data = await api.synevoControllerGetReadyByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(synevo => synevoForGrid(synevo, setRefresh));
    },

    getReadyCount: (filterText?: string, filterColumns?: string) =>
      api.synevoControllerGetReadyCount(filterText || '', filterColumns || ''),

    getForExamByPortion: async (
      examId: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      setRefresh?: () => void,
    ) => {
      const data = await api.synevoControllerGetForExamByPortion(
        examId,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(synevo => synevoForGrid(synevo, setRefresh));
    },

    getForExamCount: (
      examId: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.synevoControllerGetForExamCount(
        examId,
        filterText || '',
        filterColumns || '',
      ),

    cancelOrder: (synevoId: string, sentOrderId: string) => {
      return api.synevoControllerCancelOrder(synevoId, sentOrderId);
    },

    createForExam: (
      examId: string,
      supportingExaminationDictIds?: string[],
    ) => {
      return api.synevoControllerCreateForExam(examId, {
        supportingExaminationDictIds,
      });
    },

    getForExamSummary: async (examId: string) => {
      return api.synevoControllerGetForExamSummary(examId);
    },

    getDictCost: async (dictIds: string[]) => {
      return api.synevoControllerGetDictCost(dictIds);
    },
  };
};
